import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { methodsApi } from '../services/api';
import './MethodWorksheet.css';

interface MethodStep {
  step: number;
  text: string;
}

interface Method {
  id: string;
  name: string;
  description: string | null;
  benefit: string | null;
  instructions: MethodStep[] | null;
  debriefing: string | null;
  materials: string | null;
  theory_background: string | null;
  category_id: string | null;
  duration_minutes: number | null;
  duration_max_minutes: number | null;
  min_participants: number | null;
  max_participants: number | null;
  difficulty_level: string;
  applicable_to: string | null;
}

interface Category {
  id: string;
  name: string;
}

function formatDuration(min: number | null, max: number | null): string {
  if (!min) return '';
  if (max && max !== min) return `${min}–${max} Minuten`;
  return `${min} Minuten`;
}

function formatParticipants(min: number | null, max: number | null): string {
  if (!min && !max) return '';
  if (!max) return `ab ${min} Person(en)`;
  if (min === max) return `${min} Person(en)`;
  return `${min ?? 1}–${max} Personen`;
}

const difficultyLabels: Record<string, string> = {
  beginner: 'Einsteiger',
  intermediate: 'Fortgeschritten',
  advanced: 'Experte',
};

export default function MethodWorksheet() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [method, setMethod] = useState<Method | null>(null);
  const [category, setCategory] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([methodsApi.get(id), methodsApi.categories()])
      .then(([mRes, cRes]) => {
        setMethod(mRes.data);
        const cats: Category[] = cRes.data;
        const cat = cats.find((c) => c.id === mRes.data.category_id);
        setCategory(cat?.name || '');
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="ws-loading">
        <div className="ws-spinner"></div>
        <p>Lade Arbeitsblatt…</p>
      </div>
    );
  }

  if (error || !method) {
    return (
      <div className="ws-loading">
        <p>Methode nicht gefunden.</p>
        <button onClick={() => navigate('/methoden')}>← Zurück</button>
      </div>
    );
  }

  const duration = formatDuration(method.duration_minutes, method.duration_max_minutes);
  const participants = formatParticipants(method.min_participants, method.max_participants);
  const subtitle = method.applicable_to
    ? `für ${method.applicable_to}`
    : category
      ? category
      : '';

  return (
    <div className="ws-root">
      {/* ── Toolbar (versteckt beim Drucken) ── */}
      <div className="ws-toolbar no-print">
        <button className="ws-btn-back" onClick={() => {
          if (window.opener) {
            window.close();
          } else {
            navigate('/methoden');
          }
        }}>
          ← Zurück
        </button>
        <div className="ws-toolbar-center">
          <span className="ws-toolbar-title">Arbeitsblatt: {method.name}</span>
        </div>
        <button className="ws-btn-print" onClick={() => window.print()}>
          🖨 Drucken / Als PDF
        </button>
      </div>

      {/* ── Arbeitsblatt ── */}
      <div className="ws-page">

        {/* Akzent-Leiste */}
        <div className="ws-accent-bar">
          <span>Coaching-Methode</span>
        </div>

        {/* Header — hell, großer Titel */}
        <div className="ws-header">
          <div className="ws-header-left">
            <h1 className="ws-method-name">{method.name}</h1>
            {subtitle && <p className="ws-subtitle">{subtitle}</p>}
          </div>
          <div className="ws-header-right">
            <label className="ws-date-label">Datum:</label>
            <input type="text" className="ws-date-input" />
          </div>
        </div>

        {/* Meta-Badges */}
        <div className="ws-meta-row">
          {duration && (
            <div className="ws-meta-badge">
              <span className="ws-meta-icon">⏱</span>
              {duration}
            </div>
          )}
          {participants && (
            <div className="ws-meta-badge">
              <span className="ws-meta-icon">👥</span>
              {participants}
            </div>
          )}
          <div className="ws-meta-badge">
            <span className="ws-meta-icon">📊</span>
            {difficultyLabels[method.difficulty_level] || method.difficulty_level}
          </div>
        </div>

        {/* Inhaltskarten */}
        <div className="ws-content">

          {/* Beschreibung als Intro (wenn Benefit vorhanden) */}
          {method.description && method.benefit && (
            <p className="ws-intro">{method.description}</p>
          )}

          {/* Nutzen & Einsatzgebiet */}
          {(method.benefit || method.description) && (
            <div className="ws-card">
              <div className="ws-card-header">
                <span className="ws-card-icon">📎</span>
                <h2>Nutzen &amp; Einsatzgebiet</h2>
              </div>
              <div className="ws-card-body">
                <p>{method.benefit || method.description}</p>
              </div>
            </div>
          )}

          {/* Ablauf */}
          {method.instructions && method.instructions.length > 0 && (
            <div className="ws-card">
              <div className="ws-card-header">
                <span className="ws-card-icon">✅</span>
                <h2>Ablauf</h2>
              </div>
              <div className="ws-card-body">
                <ol className="ws-steps">
                  {method.instructions.map((s) => (
                    <li key={s.step} className="ws-step">
                      <span className="ws-step-num">{s.step}</span>
                      <span className="ws-step-text">{s.text}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}

          {/* Benötigte Materialien */}
          {method.materials && (
            <div className="ws-card">
              <div className="ws-card-header">
                <span className="ws-card-icon">📋</span>
                <h2>Benötigte Materialien</h2>
              </div>
              <div className="ws-card-body">
                <p>{method.materials}</p>
              </div>
            </div>
          )}

          {/* Reflexion & Nachbesprechung */}
          <div className="ws-card">
            <div className="ws-card-header">
              <span className="ws-card-icon">💡</span>
              <h2>Reflexion &amp; Nachbesprechung</h2>
            </div>
            <div className="ws-card-body">
              {method.debriefing && (
                <p className="ws-debrief-text">{method.debriefing}</p>
              )}
              <p className="ws-prompt">Mein <strong>nächster konkreter Schritt</strong>:</p>
              <textarea
                className="ws-textarea"
                rows={3}
                placeholder="Hier eingeben…"
              />
            </div>
          </div>

          {/* Theoriehintergrund */}
          {method.theory_background && (
            <div className="ws-card ws-card-theory">
              <div className="ws-card-header">
                <span className="ws-card-icon">📖</span>
                <h2>Theoriehintergrund</h2>
              </div>
              <div className="ws-card-body">
                <blockquote className="ws-theory-quote">
                  <p>{method.theory_background}</p>
                </blockquote>
              </div>
            </div>
          )}

        </div>{/* /ws-content */}

        {/* Footer */}
        <div className="ws-footer">
          <span>CoachDesk365 · www.coachdesk365.com</span>
          <span>Methoden-Arbeitsblatt: {method.name}</span>
        </div>

      </div>{/* /ws-page */}
    </div>
  );
}
